import { DownloadIcon, DownloadSimpleIcon, ExportIcon, SpinnerIcon } from '@phosphor-icons/react'
import { useState } from 'react'
import { AudioPreview } from './AudioPreview.tsx'
import { useExportSettingsStore } from './store.ts'
import { Modal } from './Modal.tsx'

interface DownloadAudioModalProps {
  isOpen: boolean
  onClose: () => void
  onExport: (bars: number, oversampling: 1 | 2 | 4 | 8) => Promise<void>
  onDownload: () => void
  bpm: number
  isExporting: boolean
  exportedAudioUrl: string | null
  exportedFilename: string
}

export function DownloadAudioModal({
  isOpen,
  onClose,
  onExport,
  onDownload,
  bpm,
  isExporting,
  exportedAudioUrl,
  exportedFilename,
}: DownloadAudioModalProps) {
  // Power-of-two values: 1, 2, 4, 8, 16, 32, 128, 256, 512
  const barValues = [1, 2, 4, 8, 16, 32, 128, 256, 512]
  const { exportBarsSliderIndex, exportOversampling, setExportBarsSliderIndex, setExportOversampling } =
    useExportSettingsStore()
  const sliderIndex = exportBarsSliderIndex
  const bars = barValues[sliderIndex]!
  const oversampling = exportOversampling
  const [isHoveringExport, setIsHoveringExport] = useState(false)
  const [isHoveringDownload, setIsHoveringDownload] = useState(false)

  // Calculate seconds based on bars and BPM
  // Formula: seconds = (bars * 4 beats per bar * 60 seconds per minute) / BPM
  const beatsPerBar = 4
  const seconds = (bars * beatsPerBar * 60) / bpm

  const handleExport = async () => {
    await onExport(bars, oversampling)
  }

  const durationText = (() => {
    if (seconds >= 60) {
      const total = Math.round(seconds)
      const m = Math.floor(total / 60)
      const s = total % 60
      return `${m}m:${s.toString().padStart(2, '0')}s`
    }
    return `${seconds.toFixed(2)}s`
  })()

  return (
    <>
      <style>
        {`
        @keyframes coffee-shake {
          0% { transform: translateX(0) rotate(0); }
          10% { transform: translateX(-2px) rotate(-1deg); }
          20% { transform: translateX(2px) rotate(1deg); }
          30% { transform: translateX(-2px) rotate(0); }
          40% { transform: translateX(2px) rotate(1deg); }
          50% { transform: translateX(-1px) rotate(0); }
          60% { transform: translateX(1px) rotate(-1deg); }
          70% { transform: translateX(-1px) rotate(0); }
          80% { transform: translateX(1px) rotate(1deg); }
          90% { transform: translateX(-1px) rotate(0); }
          100% { transform: translateX(0) rotate(0); }
        }
        .animate-coffee-shake {
          animation: coffee-shake 1s linear;
        }
      `}
      </style>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Export Audio"
        icon={<ExportIcon weight="regular" size={20} className="text-blue-500" />}
        disabled={isExporting}
      >
          <div className="space-y-2 mb-7">
            <div className="flex items-center justify-between">
              <label className="text-white font-semibold">Number of Bars</label>
              <span className="text-[#888] text-sm font-semibold">{bars}</span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="0"
                max={barValues.length - 1}
                step="1"
                value={sliderIndex}
                onChange={e => setExportBarsSliderIndex(parseInt(e.target.value))}
                disabled={isExporting}
                className="w-full h-2 bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              />
              <div className="absolute top-0 left-0 right-0 h-2 pointer-events-none select-none">
                {barValues.map((v, i) => {
                  // Calculate position accounting for thumb width (16px)
                  // The thumb center moves from 8px to (100% - 8px)
                  // Formula: 8px + percentage * (100% - 16px)
                  const percentage = (i / (barValues.length - 1)) * 100
                  const left = `calc(8px + ${percentage}% - ${percentage * 0.16}px)`
                  return (
                    <span key={v} className="absolute w-0" style={{ left }}>
                      <span className="inline-block -translate-x-1/2 translate-y-[14px] whitespace-nowrap text-[10px] text-[#666]">
                        {v}
                      </span>
                    </span>
                  )
                })}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-white font-semibold">Oversampling</label>
            <div className="grid grid-cols-4 gap-1">
              {([1, 2, 4, 8] as const).map(factor => (
                <button
                  key={factor}
                  onClick={() => setExportOversampling(factor)}
                  disabled={isExporting}
                  className={`px-3 py-2 text-sm font-semibold rounded-md border ${
                    oversampling === factor
                      ? 'bg-gradient-to-br from-orange-600 to-orange-500 border-orange-400 text-white'
                      : 'bg-gradient-to-br from-neutral-900 to-neutral-700 border-neutral-600 text-neutral-300 hover:opacity-90'
                  } disabled:opacity-50`}
                >
                  x{factor}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 p-3 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded">
              <div className="text-xs text-[#888] mb-1">BPM</div>
              <div className="text-sm text-white font-semibold">{bpm}</div>
            </div>
            <div className="flex-1 p-3 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded">
              <div className="text-xs text-[#888] mb-1">Bars</div>
              <div className="text-sm text-white font-semibold">{bars}</div>
            </div>
            <div className="flex-1 p-3 bg-gradient-to-br from-neutral-900 to-neutral-700 border border-[#444] rounded">
              <div className="text-xs text-[#888] mb-1">Duration</div>
              <div className="text-sm text-white font-semibold">{durationText}</div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex flex-row gap-2">
              <button
                onClick={handleExport}
                disabled={isExporting}
                onMouseEnter={() => setIsHoveringExport(true)}
                onMouseLeave={() => setIsHoveringExport(false)}
                className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-white text-black hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isExporting
                  ? (
                    <>
                      <SpinnerIcon weight="regular" size={16} className="animate-spin" />
                      <span>Processing...</span>
                    </>
                  )
                  : (
                    <>
                      <ExportIcon weight="regular" size={16} />
                      <span>Export</span>
                    </>
                  )}
              </button>

              <a
                href="https://www.buymeacoffee.com/stagas"
                target="_blank"
                rel="noopener noreferrer"
                className="flex justify-center"
              >
                <img
                  src="/buymeacoffee.png"
                  alt="Buy Me A Coffee"
                  className={isHoveringExport || isHoveringDownload ? 'animate-coffee-shake' : undefined}
                  style={{ width: '236px' }}
                />
              </a>
            </div>
            {exportedAudioUrl && (
              <div className="space-y-3">
                <AudioPreview audioUrl={exportedAudioUrl} />
                <button
                  onClick={onDownload}
                  onMouseEnter={() => setIsHoveringDownload(true)}
                  onMouseLeave={() => setIsHoveringDownload(false)}
                  className="w-full px-4 py-2 text-sm border-none rounded-md cursor-pointer font-semibold bg-gradient-to-br from-blue-600 to-blue-800 text-white hover:opacity-90 flex items-center justify-center gap-2"
                >
                  <DownloadIcon weight="regular" size={16} />
                  <span>Download &quot;{exportedFilename}&quot;</span>
                </button>
              </div>
            )}
          </div>
      </Modal>
    </>
  )
}
